import React, { useEffect, useState } from 'react';
import { adminAuthAPI } from '../services/api';
import { getReachOutBaseUrl, buildReachOutLoginUrl } from '../utils/crossAppSso';

/**
 * If Wizetok already has a session, mint a handoff JWT and open ReachOut logged in.
 * ?source=reachout → user opened ReachOut first; on miss, return to ReachOut login (never dump on Wizetok).
 * No source → user clicked Open ReachOut from Wizetok; on miss, stay on Wizetok login.
 *
 * When framed (ReachOut's login page loads this silently in a hidden iframe to
 * check for a session without a visible page bounce — see Dashboard/index.js),
 * report the result via postMessage instead of navigating: an iframe can only
 * navigate itself, not the parent tab, and a miss shouldn't be visible at all.
 */
export default function CrossAppReachOutBridge() {
  const [message, setMessage] = useState('Connecting to ReachOut…');
  const framed = window.self !== window.top;

  useEffect(() => {
    let cancelled = false;
    const source = new URLSearchParams(window.location.search).get('source') || '';

    const reportToParent = (result) => {
      if (!framed) return false;
      const targetOrigin = getReachOutBaseUrl();
      if (!targetOrigin) return false;
      window.parent.postMessage({ type: 'reachout-bridge-result', ...result }, targetOrigin);
      return true;
    };

    (async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        if (cancelled) return;
        if (reportToParent({ ok: false })) return;
        if (source === 'reachout') {
          window.location.replace(`${getReachOutBaseUrl()}/login?skip_bridge=1`);
        } else {
          window.location.replace('/admin/login?skip_bridge=1');
        }
        return;
      }

      try {
        const { data } = await adminAuthAPI.createHandoff('reachout');
        const handoff = data?.handoffToken;
        if (!handoff) throw new Error('Handoff token missing');
        if (cancelled) return;
        const redirectUrl = buildReachOutLoginUrl({ handoffToken: handoff });
        if (reportToParent({ ok: true, redirectUrl })) return;
        window.location.replace(redirectUrl);
      } catch (e) {
        console.error('[sso/reachout] bridge failed', e);
        if (cancelled) return;
        if (reportToParent({ ok: false })) return;
        setMessage('Could not open ReachOut automatically. Opening login…');
        window.setTimeout(() => {
          const fallback =
            source === 'reachout'
              ? `${getReachOutBaseUrl()}/login?skip_bridge=1`
              : '/admin/login?skip_bridge=1';
          window.location.assign(fallback);
        }, 800);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [framed]);

  if (framed) return null;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#f8fafc',
        color: '#334155',
        fontFamily: 'system-ui, sans-serif',
        fontSize: 15,
      }}
    >
      {message}
    </div>
  );
}
